import React, { useMemo } from 'react';
import { Monitor, Mail, FileText, Target, TrendingUp, Settings, BarChart, CheckCircle2, Home, Users, Star, Moon, HelpCircle, Smartphone, Layers, Sparkles, Heart, UserCheck, ShoppingBag } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { Badge } from '../components/ui/Badge';
import { ALL_AD_SLOTS, AdSlotConfig } from '../components/ads/adData';
interface AdvertisingPageProps {
  onNavigate: (page: string) => void;
}
export function AdvertisingPage({
  onNavigate
}: AdvertisingPageProps) {
  const {
    success
  } = useToast();
  const groupedSlots = useMemo(() => {
    return ALL_AD_SLOTS.reduce((acc, slot) => {
      let pageGroup = slot.page;
      if (pageGroup === 'Блог (статья)') pageGroup = 'Блог';
      if (!acc[pageGroup]) {
        acc[pageGroup] = [];
      }
      acc[pageGroup].push(slot);
      return acc;
    }, {} as Record<string, AdSlotConfig[]>);
  }, []);
  const getPageIcon = (page: string) => {
    switch (page) {
      case 'Каталог астрологов':
        return <Users className="w-5 h-5 text-blue-500" data-id="element-20996" />;
      case 'Блог':
        return <FileText className="w-5 h-5 text-emerald-500" data-id="element-20997" />;
      case 'Гороскоп':
        return <Star className="w-5 h-5 text-amber-500" data-id="element-20998" />;
      case 'Бесплатная карта':
        return <Sparkles className="w-5 h-5 text-violet-500" data-id="element-20999" />;
      case 'Совместимость':
        return <Heart className="w-5 h-5 text-rose-500" data-id="element-21000" />;
      case 'Лунный календарь':
        return <Moon className="w-5 h-5 text-slate-500" data-id="element-21001" />;
      case 'Профиль астролога':
        return <UserCheck className="w-5 h-5 text-indigo-500" data-id="element-21002" />;
      case 'Детали услуги':
        return <Layers className="w-5 h-5 text-purple-500" data-id="element-21003" />;
      case 'FAQ':
        return <HelpCircle className="w-5 h-5 text-rose-500" data-id="element-21004" />;
      case 'Маркетплейс':
        return <ShoppingBag className="w-5 h-5 text-orange-500" data-id="element-21005" />;
      case 'Отчёт':
        return <FileText className="w-5 h-5 text-cyan-500" data-id="element-21006" />;
      default:
        return <FileText className="w-5 h-5 text-slate-500" data-id="element-21007" />;
    }
  };
  const getPageColor = (page: string) => {
    switch (page) {
      case 'Каталог астрологов':
        return 'bg-blue-50 border-blue-100';
      case 'Блог':
        return 'bg-emerald-50 border-emerald-100';
      case 'Гороскоп':
        return 'bg-amber-50 border-amber-100';
      case 'Бесплатная карта':
        return 'bg-violet-50 border-violet-100';
      case 'Совместимость':
        return 'bg-rose-50 border-rose-100';
      case 'Лунный календарь':
        return 'bg-slate-50 border-slate-200';
      case 'Профиль астролога':
        return 'bg-indigo-50 border-indigo-100';
      case 'Детали услуги':
        return 'bg-purple-50 border-purple-100';
      case 'FAQ':
        return 'bg-rose-50 border-rose-100';
      case 'Маркетплейс':
        return 'bg-orange-50 border-orange-100';
      case 'Отчёт':
        return 'bg-cyan-50 border-cyan-100';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };
  const getTypeBadgeVariant = (type: string): 'neutral' | 'info' | 'pro' | 'default' => {
    switch (type) {
      case 'Banner':
        return 'neutral';
      case 'Native':
        return 'info';
      case 'Pinned':
        return 'pro';
      default:
        return 'default';
    }
  };
  const formatPrice = (price: number) => {
    return price.toLocaleString('ru-RU') + ' ₽';
  };
  return <div className="w-full bg-slate-50 pb-24" data-id="element-21008">
      <div className="bg-white border-b border-slate-200 pt-8 pb-16" data-id="element-21009">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21010">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Реклама'
        }]} className="mb-8" data-id="element-21011" />
          <div className="text-center max-w-4xl mx-auto mt-8" data-id="element-21012">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-21013">
              Реклама на платформе
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-21014">
              Доступ к целевой аудитории, заинтересованной в самопознании,
              психологии и эзотерике. Более 100 000 уникальных посетителей в
              месяц.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-21015">
        {/* Audience Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20" data-id="element-21016">
          <Card padding="lg" className="text-center" data-id="element-21017">
            <div className="text-3xl font-bold text-primary mb-2" data-id="element-21018">120 000+</div>
            <div className="text-sm text-slate-600" data-id="element-21019">
              уникальных посетителей/мес
            </div>
          </Card>
          <Card padding="lg" className="text-center" data-id="element-21020">
            <div className="text-3xl font-bold text-primary mb-2" data-id="element-21021">85%</div>
            <div className="text-sm text-slate-600" data-id="element-21022">женщины 25-45 лет</div>
          </Card>
          <Card padding="lg" className="text-center" data-id="element-21023">
            <div className="text-3xl font-bold text-primary mb-2" data-id="element-21024">4.2 мин</div>
            <div className="text-sm text-slate-600" data-id="element-21025">средняя сессия</div>
          </Card>
          <Card padding="lg" className="text-center" data-id="element-21026">
            <div className="text-3xl font-bold text-primary mb-2" data-id="element-21027">45 000</div>
            <div className="text-sm text-slate-600" data-id="element-21028">email-подписчиков</div>
          </Card>
        </div>

        <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-21029">
          Рекламные форматы
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-21030">
          <Card padding="lg" className="flex flex-col" data-id="element-21031">
            <div className="w-full h-32 bg-slate-100 rounded-xl mb-6 flex flex-col items-center justify-center border border-slate-200 relative overflow-hidden" data-id="element-21032">
              {/* Mockup visual */}
              <div className="w-full h-4 bg-slate-200 absolute top-0" data-id="element-21033"></div>
              <div className="w-3/4 h-12 bg-indigo-100 border border-indigo-200 rounded flex items-center justify-center text-indigo-500 text-xs font-medium" data-id="element-21034">
                Место для баннера
              </div>
              <div className="w-full h-8 bg-slate-200 absolute bottom-0 flex gap-2 px-4 items-center" data-id="element-21035">
                <div className="w-8 h-8 rounded-full bg-slate-300" data-id="element-21036"></div>
                <div className="w-16 h-2 bg-slate-300 rounded" data-id="element-21037"></div>
              </div>
            </div>

            <div className="flex items-center mb-3" data-id="element-21038">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-primary mr-3" data-id="element-21039">
                <Monitor className="w-4 h-4" data-id="element-21040" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-21041">Баннер</h3>
            </div>
            <p className="text-slate-600 text-sm mb-6 flex-grow" data-id="element-21042">
              Классический медийный формат для максимального охвата аудитории на
              ключевых страницах.
            </p>

            <div className="bg-slate-50 p-4 rounded-xl" data-id="element-21043">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2" data-id="element-21044">
                Спецификации
              </div>
              <ul className="space-y-1 text-sm text-slate-700" data-id="element-21045">
                <li data-id="element-21046">
                  <span className="text-slate-500" data-id="element-21047">Размеры:</span> 728x90px,
                  300x600px, 300x250px
                </li>
                <li data-id="element-21048">
                  <span className="text-slate-500" data-id="element-21049">Форматы:</span> JPG, PNG, GIF
                </li>
                <li data-id="element-21050">
                  <span className="text-slate-500" data-id="element-21051">Вес:</span> до 500 КБ
                </li>
              </ul>
            </div>
          </Card>

          <Card padding="lg" className="flex flex-col" data-id="element-21052">
            <div className="w-full h-32 bg-slate-100 rounded-xl mb-6 flex flex-col items-center justify-center border border-slate-200 relative overflow-hidden" data-id="element-21053">
              {/* Mockup visual */}
              <div className="w-full h-full flex p-4 gap-4" data-id="element-21054">
                <div className="w-1/3 h-full bg-slate-200 rounded" data-id="element-21055"></div>
                <div className="w-2/3 flex flex-col gap-2" data-id="element-21056">
                  <div className="w-full h-4 bg-slate-300 rounded" data-id="element-21057"></div>
                  <div className="w-3/4 h-3 bg-slate-200 rounded" data-id="element-21058"></div>
                  <div className="w-full h-12 bg-amber-100 border border-amber-200 rounded mt-auto flex items-center justify-center text-amber-600 text-xs font-medium" data-id="element-21059">
                    Нативная вставка
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center mb-3" data-id="element-21060">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 mr-3" data-id="element-21061">
                <FileText className="w-4 h-4" data-id="element-21062" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-21063">
                Нативная интеграция
              </h3>
            </div>
            <p className="text-slate-600 text-sm mb-6 flex-grow" data-id="element-21064">
              Органичное встраивание в контент сайта. Не вызывает баннерной
              слепоты и дает высокую конверсию.
            </p>

            <div className="bg-slate-50 p-4 rounded-xl" data-id="element-21065">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2" data-id="element-21066">
                Особенности
              </div>
              <ul className="space-y-1 text-sm text-slate-700" data-id="element-21067">
                <li data-id="element-21068">
                  <span className="text-slate-500" data-id="element-21069">Вид:</span> Карточка в стиле
                  сайта
                </li>
                <li data-id="element-21070">
                  <span className="text-slate-500" data-id="element-21071">Контент:</span> Заголовок,
                  текст, кнопка
                </li>
                <li data-id="element-21072">
                  <span className="text-slate-500" data-id="element-21073">Размещение:</span> Между
                  блоками контента
                </li>
              </ul>
            </div>
          </Card>

          <Card padding="lg" className="flex flex-col" data-id="element-21074">
            <div className="w-full h-32 bg-slate-100 rounded-xl mb-6 flex flex-col items-center justify-center border border-slate-200 relative overflow-hidden" data-id="element-21075">
              {/* Mockup visual */}
              <div className="w-2/3 h-full bg-white shadow-sm border border-slate-200 flex flex-col p-2 gap-2" data-id="element-21076">
                <div className="w-full h-3 bg-slate-200 rounded" data-id="element-21077"></div>
                <div className="w-full h-12 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-center text-emerald-600 text-[10px] font-medium text-center leading-tight" data-id="element-21078">
                  Ваш блок
                  <br data-id="element-21079" />в рассылке
                </div>
                <div className="w-full h-2 bg-slate-100 rounded mt-auto" data-id="element-21080"></div>
              </div>
            </div>

            <div className="flex items-center mb-3" data-id="element-21081">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 mr-3" data-id="element-21082">
                <Mail className="w-4 h-4" data-id="element-21083" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-21084">
                Email-рассылка
              </h3>
            </div>
            <p className="text-slate-600 text-sm mb-6 flex-grow" data-id="element-21085">
              Прямой контакт с лояльной аудиторией через еженедельный дайджест
              или отдельное письмо.
            </p>

            <div className="bg-slate-50 p-4 rounded-xl" data-id="element-21086">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2" data-id="element-21087">
                Метрики
              </div>
              <ul className="space-y-1 text-sm text-slate-700" data-id="element-21088">
                <li data-id="element-21089">
                  <span className="text-slate-500" data-id="element-21090">База:</span> 45 000+
                  подписчиков
                </li>
                <li data-id="element-21091">
                  <span className="text-slate-500" data-id="element-21092">Open Rate:</span> ~28%
                </li>
                <li data-id="element-21093">
                  <span className="text-slate-500" data-id="element-21094">CTR:</span> ~4.5%
                </li>
              </ul>
            </div>
          </Card>
        </div>

        {/* Unified Pricing Section */}
        <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center" data-id="element-21095">
          Рекламные слоты и цены
        </h2>
        <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12" data-id="element-21096">
          17 рекламных слотов на различных страницах платформы для точного
          таргетинга вашей аудитории.
        </p>

        <Card padding="none" className="mb-12 overflow-hidden shadow-md" data-id="element-21097">
          <div className="flex flex-col" data-id="element-21098">
            {Object.entries(groupedSlots).map(([page, slots]) => <div key={page} className="flex flex-col" data-id="element-21099">
                {/* Group Header */}
                <div className={`py-2.5 px-4 flex items-center justify-between ${getPageColor(page)} border-y border-slate-200/50 first:border-t-0`} data-id="element-21100">
                  <div className="flex items-center gap-2" data-id="element-21101">
                    <div className="w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4" data-id="element-21102">
                      {getPageIcon(page)}
                    </div>
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-21103">
                      {page}
                    </h3>
                  </div>
                  <Badge variant="neutral" size="sm" data-id="element-21104">
                    {slots.length} слота
                  </Badge>
                </div>

                {/* Slots List */}
                <div className="divide-y divide-slate-100" data-id="element-21105">
                  {slots.map(slot => <div key={slot.id} className="py-2.5 px-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-21106">
                      <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-21107">
                        <span className="font-medium text-slate-800 text-sm truncate" data-id="element-21108">
                          {slot.name}
                        </span>
                        <Badge variant={getTypeBadgeVariant(slot.type)} size="sm" className="hidden sm:inline-flex" data-id="element-21109">
                          {slot.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 ml-4" data-id="element-21110">
                        <span className="text-xs text-slate-400 w-20 text-right hidden sm:block truncate" data-id="element-21111">
                          {slot.size}
                        </span>
                        <div className="text-sm font-semibold text-slate-900 w-28 text-right" data-id="element-21112">
                          {formatPrice(slot.basePrice)}
                          <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-21113">
                            /мес
                          </span>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </Card>

        {/* Special Formats */}
        <Card padding="none" className="mb-24 overflow-hidden border-indigo-200 shadow-md" data-id="element-21114">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 py-3 px-4 border-b border-indigo-100 flex items-center gap-3" data-id="element-21115">
            <Target className="w-5 h-5 text-indigo-600" data-id="element-21116" />
            <div data-id="element-21117">
              <h3 className="font-bold text-slate-900 text-base" data-id="element-21118">
                Спецформаты
              </h3>
              <p className="text-xs text-slate-600" data-id="element-21119">
                Индивидуальные размещения и рассылки
              </p>
            </div>
          </div>
          <div className="divide-y divide-slate-100 bg-white" data-id="element-21120">
            {/* Row 1 */}
            <div className="py-2.5 px-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-21121">
              <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-21122">
                <span className="font-medium text-slate-800 text-sm truncate" data-id="element-21123">
                  Нативная статья
                </span>
                <Badge variant="info" size="sm" className="hidden sm:inline-flex" data-id="element-21124">
                  Native
                </Badge>
              </div>
              <div className="flex items-center gap-4 shrink-0 ml-4" data-id="element-21125">
                <span className="text-xs text-slate-500 hidden md:block truncate max-w-[250px]" data-id="element-21126">
                  Публикация в блоге + Анонс в Telegram. Размещение навсегда.
                </span>
                <div className="text-sm font-semibold text-slate-900 w-28 text-right" data-id="element-21127">
                  35 000 ₽
                  <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-21128">
                    /статья
                  </span>
                </div>
              </div>
            </div>
            {/* Row 2 */}
            <div className="py-2.5 px-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-21129">
              <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-21130">
                <span className="font-medium text-slate-800 text-sm truncate" data-id="element-21131">
                  Email-рассылка
                </span>
                <Badge variant="neutral" size="sm" className="hidden sm:inline-flex" data-id="element-21132">
                  Отдельное письмо
                </Badge>
              </div>
              <div className="flex items-center gap-4 shrink-0 ml-4" data-id="element-21133">
                <span className="text-xs text-slate-500 hidden md:block truncate max-w-[250px]" data-id="element-21134">
                  Моно-рассылка по всей базе (45 000+ подписчиков).
                </span>
                <div className="text-sm font-semibold text-slate-900 w-28 text-right" data-id="element-21135">
                  40 000 ₽
                  <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-21136">
                    /выпуск
                  </span>
                </div>
              </div>
            </div>
            {/* Row 3 */}
            <div className="py-2.5 px-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-21137">
              <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-21138">
                <span className="font-medium text-slate-800 text-sm truncate" data-id="element-21139">
                  Email-рассылка
                </span>
                <Badge variant="neutral" size="sm" className="hidden sm:inline-flex" data-id="element-21140">
                  Блок в дайджесте
                </Badge>
              </div>
              <div className="flex items-center gap-4 shrink-0 ml-4" data-id="element-21141">
                <span className="text-xs text-slate-500 hidden md:block truncate max-w-[250px]" data-id="element-21142">
                  Нативный блок в еженедельном дайджесте (45 000+ подписчиков).
                </span>
                <div className="text-sm font-semibold text-slate-900 w-28 text-right" data-id="element-21143">
                  25 000 ₽
                  <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-21144">
                    /выпуск
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Why Us */}
        <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-21145">
          Почему мы?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24" data-id="element-21146">
          <Card padding="lg" data-id="element-21147">
            <Target className="w-8 h-8 text-primary mb-4" data-id="element-21148" />
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21149">Целевая аудитория</h3>
            <p className="text-sm text-slate-600" data-id="element-21150">
              Платежеспособная аудитория, активно интересующаяся саморазвитием.
            </p>
          </Card>
          <Card padding="lg" data-id="element-21151">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-4" data-id="element-21152" />
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21153">
              Высокая вовлечённость
            </h3>
            <p className="text-sm text-slate-600" data-id="element-21154">
              Пользователи проводят на сайте много времени, изучая объемные
              отчеты.
            </p>
          </Card>
          <Card padding="lg" data-id="element-21155">
            <Settings className="w-8 h-8 text-amber-500 mb-4" data-id="element-21156" />
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21157">Гибкие форматы</h3>
            <p className="text-sm text-slate-600" data-id="element-21158">
              Подберем оптимальный формат под ваши задачи и бюджет.
            </p>
          </Card>
          <Card padding="lg" data-id="element-21159">
            <BarChart className="w-8 h-8 text-indigo-500 mb-4" data-id="element-21160" />
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21161">
              Прозрачная аналитика
            </h3>
            <p className="text-sm text-slate-600" data-id="element-21162">
              Предоставляем подробные отчеты по показам и кликам после кампании.
            </p>
          </Card>
        </div>

        {/* Contact Form */}
        <Card padding="lg" className="max-w-3xl mx-auto border-none shadow-xl" data-id="element-21163">
          <div className="text-center mb-8" data-id="element-21164">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-21165">
              Оставить заявку
            </h2>
            <p className="text-slate-600" data-id="element-21166">
              Заполните форму, и наш менеджер свяжется с вами для обсуждения
              деталей.
            </p>
          </div>

          <form className="space-y-6" data-id="element-21167">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-21168">
              <Input label="Компания / Бренд" placeholder="Название вашей компании" data-id="element-21169" />
              <Input label="Контактное лицо" placeholder="Ваше имя" data-id="element-21170" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-21171">
              <Input label="Email" type="email" placeholder="work@example.com" data-id="element-21172" />
              <Input label="Телефон" type="tel" placeholder="+7 (999) 000-00-00" data-id="element-21173" />
            </div>

            <div data-id="element-21174">
              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-21175">
                Ориентировочный бюджет
              </label>
              <select className="block w-full rounded-xl border-slate-200 shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-4 py-2.5 border outline-none text-slate-900 bg-white appearance-none" data-id="element-21176">
                <option value="" data-id="element-21177">Выберите бюджет...</option>
                <option value="under50" data-id="element-21178">До 50 000 ₽</option>
                <option value="50to200" data-id="element-21179">50 000 ₽ - 200 000 ₽</option>
                <option value="over200" data-id="element-21180">Более 200 000 ₽</option>
              </select>
            </div>

            <div data-id="element-21181">
              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-21182">
                Комментарий или вопросы
              </label>
              <textarea rows={4} className="block w-full rounded-xl border-slate-200 shadow-sm focus:ring-primary focus:border-primary sm:text-sm px-4 py-2.5 border outline-none text-slate-900 resize-none" placeholder="Опишите ваш продукт и цели рекламной кампании..." data-id="element-21183"></textarea>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer group" data-id="element-21184">
              <input type="checkbox" required className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-21185" />
              <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-700 transition-colors" data-id="element-21186">
                Я соглашаюсь с{' '}
                <button type="button" onClick={() => onNavigate('public-offer')} className="text-indigo-600 hover:underline" data-id="element-21187">
                  публичной офертой
                </button>{' '}
                и{' '}
                <button type="button" onClick={() => onNavigate('privacy')} className="text-indigo-600 hover:underline" data-id="element-21188">
                  политикой конфиденциальности
                </button>
              </span>
            </label>

            <div className="pt-4" data-id="element-21189">
              <Button size="lg" fullWidth type="button" onClick={() => {
              success('Заявка на рекламу отправлена! Мы свяжемся с вами в течение 24 часов.');
            }} data-id="element-21190">
                Отправить заявку
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4" data-id="element-21191">
                Мы свяжемся с вами в течение 1 рабочего дня
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>;
}